import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Title from '../../commonComponents/Title';
import translate from '../../commonComponents/translate';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import JourneyBanner from '../academy/list/journeyBanner/JourneyBanner';
import ShopSearchContent from './shopItems/ShopSearchContent';
import JourneyProp from '../journey/propTypes/journeyProp';
import AttributeProp from './propTypes/attributeProp';
import ShopSubMenu from './shopItems/ShopSubMenu';
import MetaContext from '../../contextProviders/MetaContext';
import ShopSubHeader from './ShopSubHeader';
import ShopCategoryFilter from './ShopCategoryFilter';
import ShopMobileFilters from './ShopMobileFilters';
import MarketInfosContext from '../../contextProviders/MarketInfosContext';

function SearchPage({
  t,
  journeys,
  catalogTypes,
  attributes,
  priceRange,
  searchInput,
}) {
  const { userPortalMenus } = useContext(MetaContext);
  const { localLanguage } = useContext(MarketInfosContext);
  const title =
    userPortalMenus.find(u => u.type === 'marketplace')?.name ||
    t('shop.market_place');
  const [categories, setCategories] = useState([]);
  const [attrs, setAttrs] = useState(attributes);
  const [query, setQuery] = useState({
    category: '',
    maxPrice: priceRange.max,
    attributes: {},
    sort: 'name',
    search: searchInput,
    productType: catalogTypes.length ? catalogTypes[0].catalogType : undefined,
  });

  const handleSearchChange = search => {
    setQuery(q => ({ ...q, search: `${search}` }));
  };

  const handleQueryChange = value => {
    setQuery(q => ({ ...q, ...value }));
  };

  const getCategories = async () => {
    await axios
      .get(`/api/market/product/category/list?language=${localLanguage}`, {
        params: {
        },
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      })
      .then(({ data: response }) => {
        const filteredCategories = response.data.filter(
          category => category.productsInCategory > 0,
        );
        setCategories(filteredCategories);
      })
      .catch(err => {
        console.log(err);
      });
  };

  const getAttributes = categoryId => {
    axios
      .get('/shop/attributes', {
        params: { category: categoryId, language: localLanguage },
      })
      .then(response => {
        setAttrs(response.data.data);
      });
  };

  useEffect(() => {
    setQuery(q => ({ ...q, sort: 'name' }));
    getCategories();
  }, []);

  useEffect(() => {
    getAttributes(query.category);
  }, [query.category]);

  return (
    <Title title={title}>
      <ContentContainer>
        <ShopSubHeader
          title={title}
          handleSearchChange={handleSearchChange}
          query={query}
          t={t}
        />
        <div className="m-content shop">
          <div className="shopWebFilters">
            <div className="row">
              <div className="col-lg-12">
                <ShopSubMenu
                  t={t}
                  catalogTypes={catalogTypes}
                  categories={categories}
                  query={query}
                  handleQueryChange={handleQueryChange}
                />
              </div>
            </div>
            <div className="row my-3">
              <div className="col-lg-12">
                <ShopCategoryFilter
                  attributes={attrs}
                  selected={query}
                  onChange={handleQueryChange}
                  priceRange={priceRange}
                  t={t}
                />
              </div>
            </div>
          </div>
          <div className="row shopProducts">
            <div className="col-lg-12">
              <div className="shop-banner m--margin-top-10">
                {journeys?.length > 0 && (
                  <JourneyBanner t={t} journey={journeys[0]} />
                )}
              </div>
              <div className="row fix-on-mobile">
                <div className="col-12">
                  <ShopMobileFilters
                    catalogTypes={catalogTypes}
                    categories={categories}
                    attributes={attrs}
                    query={query}
                    handleQueryChange={handleQueryChange}
                    handleSearchChange={handleSearchChange}
                    priceRange={priceRange}
                    t={t}
                  />
                </div>
                <div id="shopSearchContentDiv" className="col-lg-12">
                  <ShopSearchContent t={t} query={query} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

SearchPage.propTypes = {
  t: PropTypes.func.isRequired,
  journeys: PropTypes.arrayOf(PropTypes.shape(JourneyProp.propType)).isRequired,
  catalogTypes: PropTypes.arrayOf(
    PropTypes.shape({
      catalogType: PropTypes.string,
      total: PropTypes.number,
    }),
  ).isRequired,
  attributes: PropTypes.arrayOf(PropTypes.shape(AttributeProp.propType))
    .isRequired,
  priceRange: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
    currency: PropTypes.string,
    symbol: PropTypes.string,
  }),
  searchInput: PropTypes.string,
};

SearchPage.defaultProps = {
  priceRange: PropTypes.shape({
    min: 0,
    max: 10000,
    currency: 'EUR',
    symbol: '€',
  }),
  searchInput: '',
};

export default translate(SearchPage);
